<script setup lang="ts">
defineProps<{ data: AccordionProps; template?: 'page' | 'overlay' }>()

const mediaRef = ref<HTMLElement | null>(null)
const container = ref<HTMLElement | null>(null)
const { $gsap } = useNuxtApp()

const isOpen = ref(false)
const { width: innerWidth } = useWindowSize()
const { isMobileOrTablet } = useDevice()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})

let openHeight = 0
let closedHeight = 0
const tl = $gsap?.timeline({ paused: true })

const updateHeights = () => {
  if (mediaRef.value) {
    openHeight = mediaRef.value.scrollHeight
    closedHeight = 0

    if (!isOpen.value) {
      $gsap.set(mediaRef.value, { height: closedHeight })
    } else {
      $gsap.set(mediaRef.value, { height: openHeight })
    }

    // Update timeline with new height
    tl.clear()
    tl.to(mediaRef.value, {
      height: openHeight,
      duration: 0.6,
      ease: 'power2.inOut',
      onComplete: () => {
        if (mediaRef.value) mediaRef.value.style.height = 'auto'
      },
      onReverseComplete: () => {
        if (mediaRef.value) mediaRef.value.style.height = '0px'
      },
    })

    if (isOpen.value) tl.progress(1)
  }
}

const handleResize = () => {
  updateHeights()
}

onMounted(() => {
  updateHeights()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

const handleHoverIn = () => {
  if (!isMobile.value) {
    tl.play()
    isOpen.value = true
  }
}
const handleHoverOut = () => {
  if (!isMobile.value) {
    tl.reverse()
    isOpen.value = false
  }
}
const toggleAnimation = () => {
  if (isMobile.value) {
    if (isOpen.value) {
      tl.reverse()
    } else {
      tl.play()
    }
    isOpen.value = !isOpen.value
  }
}
</script>

<template>
  <section
    ref="container"
    class="mx-bleed gap-gap mt-4 grid h-full grid-cols-3 border-t border-black/50 py-8 lg:grid-cols-4"
    :class="{
      'bg-white': template !== 'page' && template !== 'overlay' && data.theme !== 'light-grey',
      'bg-light-grey':
        template === 'page' || (template !== 'overlay' && data.theme === 'light-grey'),
    }"
    @mouseenter="handleHoverIn"
    @mouseleave="handleHoverOut"
    @click="toggleAnimation"
  >
    <p class="font-display text-2xl uppercase font-stretch-expanded select-none">
      {{ data.indicator }}
    </p>

    <button class="gap-x-bleed col-span-2 grid gap-y-5 text-left lg:col-span-3 lg:grid-cols-3">
      <div class="flex flex-col gap-5 lg:col-span-2">
        <p class="font-display text-xl uppercase font-stretch-expanded">{{ data.title }}</p>
        <ElementsPortableTextWrapper class="text-lg" :content="data.subHeading" />
        <ElementsPortableTextWrapper v-if="!isMobile" :content="data.text" />
      </div>

      <div ref="mediaRef" class="grid gap-5 overflow-clip lg:col-start-1 lg:row-start-1">
        <ElementsPortableTextWrapper v-if="isMobile" :content="data.text" />
        <CommonMedia :sizes="['30vw', '15vw']" :media="data.media" />
      </div>
      <div
        v-if="isMobile && (data.text || data.media)"
        class="p-bleed text-neon-yellow flex size-10 h-fit w-fit items-center justify-center bg-black"
        aria-label="Open accordion"
      >
        <transition name="rotate-fade" mode="out-in">
          <IconsArrowS v-if="!isOpen" />
          <IconsArrowN v-else />
        </transition>
      </div>
    </button>
  </section>
</template>

<style scoped>
.rotate-fade-enter-active,
.rotate-fade-leave-active {
  transition: all 0.2s ease-in-out;
  transform-origin: center center;
}

.rotate-fade-enter-from,
.rotate-fade-leave-to {
  rotate: 90deg;
}
</style>
